import type { FundingApplicationSchemas } from "@wayflyer/api-types";

import { validateAccountingPeriods } from "../utils/validate-accounting-periods";

type AccountingDocTypeStatus = FundingApplicationSchemas.ValidatedFile["validationStatus"];

const READY_TO_VALIDATE = "ready";
const VALIDATING = "validating";
const VALIDATED = "validated";
const FAILED_VALIDATION = "failed-validation";

const unique = <T>(arr: T[]) => [...new Set(arr)];

/** Assign a validation status and aggregate issues for a particualar doc type */
export const useAccountingDocumentsValidation = ({
  docs,
}: {
  docs: FundingApplicationSchemas.ValidatedFile[];
}): AccountingDocumentsValidationResult => {
  if (
    docs.every((doc) => doc.customerVerified) || // documents are verified by user, no further checks needed
    docs.filter((doc) => doc.validationStatus === FAILED_VALIDATION).length > 3 // too many issues, just skip
  ) {
    return { status: VALIDATED, globalIssues: [], issuesByFileId: {}, files: docs };
  }

  let status: AccountingDocTypeStatus = VALIDATING;

  const globalIssues: string[] = [];
  const issuesByFileId = docs.reduce((record, doc) => {
    if (doc.validatorIssues.length) {
      record[doc.id] = doc.validatorIssues;
    }
    return record;
  }, {} as Record<string, FundingApplicationSchemas.ValidatorIssue[]>);

  if (docs.every((doc) => doc.validationStatus === VALIDATED)) {
    status = VALIDATED;
  }

  if (docs.some((doc) => doc.validationStatus === FAILED_VALIDATION)) {
    status = FAILED_VALIDATION;
  } else if (
    !docs.some(
      (doc) => doc.validationStatus === VALIDATING || doc.validationStatus === READY_TO_VALIDATE
    ) /* ensure all docs went through the validator before checking periods */
  ) {
    const extractedMonths = unique(docs.flatMap((doc) => doc.validatorExtractedMonthPeriods));
    const { errors } = validateAccountingPeriods(extractedMonths);

    if (errors.length) {
      status = FAILED_VALIDATION;
      globalIssues.push(...errors);
    }
  }

  return { status, globalIssues, issuesByFileId, files: docs };
};

export type AccountingDocumentsValidationResult = {
  status: "validating" | "validated" | "failed-validation";
  globalIssues: string[];
  issuesByFileId: Record<string, FundingApplicationSchemas.ValidatorIssue[]>;
  files: FundingApplicationSchemas.ValidatedFile[];
};
